import { Fragment } from 'react';
import {
    retrognathismOptions,
    tongueSizeOptions,
    tonsilSizeOptions,
} from '@frontend/components/pages/episodeCategory/customControls/osas/osasReferral/components/ImageSelect';
import type { IDiagnosticReportGetOne } from '../../../../interfaces/diagnosticReportGetOne';
import type { IOsasc1CustomControlData } from './Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { ComorbiditiesChoice } from '../../../../../../common/enums/comorbiditiesChoice';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { translateKey } from '../../../../../translate';
import { FormDivider, FormInlineGroup, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { renderBmi } from '../../OSASV/v1/OsasvV1RequestRead';
import { anamnesisFieldsKsyosSleep, anamnesisFieldsNsi } from './anamnesisFields';

export interface ICustomControlDiagnosticReportReadCustomControlProps {
    customControlData: IOsasc1CustomControlData;
    diagnosticReport?: IDiagnosticReportGetOne;
}

export function Osasc1V1DiagnosticReportReadCustomControlFields(
    props: ICustomControlDiagnosticReportReadCustomControlProps
) {
    return (
        <>
            {showAnamnesisFields()}

            {props.customControlData.anamnese_overig && (
                <HorizontalFormGroupRead>
                    <FormLabel>Anamnese overig</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.anamnese_overig}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}
            {props.customControlData.comorbiditeitenChoice !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Comorbiditeiten</FormLabel>
                    <InputContainer>
                        <FormInlineGroup value={props.customControlData.comorbiditeitenChoice} />
                        {props.customControlData.comorbiditeitenChoice === ComorbiditiesChoice.ja && (
                            <InputContainer>
                                {props.customControlData.comorbiditeiten.hoge_bloeddruk && (
                                    <FormInlineGroup value="Hoge bloeddruk" />
                                )}
                                {props.customControlData.comorbiditeiten.cardiale_problematiek && (
                                    <FormInlineGroup value="Cardiale problematiek" />
                                )}
                                {props.customControlData.comorbiditeiten.diabetes_mellitus && (
                                    <FormInlineGroup value="Diabetes mellitus" />
                                )}
                                {props.customControlData.comorbiditeiten.burn_out_of_depressieve_klachten && (
                                    <FormInlineGroup value="Burn-out of depressieve klachten" />
                                )}
                                {props.customControlData.comorbiditeiten.anders_text && (
                                    <FormInlineGroup value={props.customControlData.comorbiditeiten.anders_text} />
                                )}
                            </InputContainer>
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {(props.customControlData.consultationPlace ||
                props.customControlData.tonsilSize ||
                props.customControlData.tongueSize ||
                props.customControlData.retrognathism) && (
                <>
                    <FormDivider />
                    <HeaderDivider text={translateKey('physicalExamination.physicalExamination')} />
                </>
            )}

            {props.customControlData.consultationPlace && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('physicalExamination.consultationPlace')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.consultationPlace}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.customControlData.tonsilSize && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('physicalExamination.tonsilSize')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.tonsilSize}</FormParagraph>
                        {props.customControlData.somnologyOrganizationType ===
                            SomnologyOrganizationType.ksyosMedicalCenter &&
                            showPhysicalExaminationMiniature(
                                tonsilSizeOptions,
                                props.customControlData.tonsilSize,
                                'tonsilSize'
                            )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.customControlData.tongueSize && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('physicalExamination.tongueSize')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.tongueSize}</FormParagraph>
                        {props.customControlData.somnologyOrganizationType ===
                            SomnologyOrganizationType.ksyosMedicalCenter &&
                            showPhysicalExaminationMiniature(
                                tongueSizeOptions,
                                props.customControlData.tongueSize,
                                'tongueSize'
                            )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.customControlData.retrognathism && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('physicalExamination.retrognathism')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.retrognathism}</FormParagraph>
                        {props.customControlData.somnologyOrganizationType ===
                            SomnologyOrganizationType.ksyosMedicalCenter &&
                            showPhysicalExaminationMiniature(
                                retrognathismOptions,
                                props.customControlData.retrognathism,
                                'retrognathism'
                            )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {(props.customControlData.poligraphyExam || (props.diagnosticReport && props.diagnosticReport.remark)) && (
                <>
                    <FormDivider />
                    <HeaderDivider text={translateKey('diagnosticReport.policy')} />
                    {props.customControlData.poligraphyExam && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Polygrafie onderzoek?</FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.customControlData.poligraphyExam}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {props.diagnosticReport && props.diagnosticReport.remark && (
                        <HorizontalFormGroupRead>
                            <FormLabel>{translateKey('common.remarks')}</FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.diagnosticReport.remark}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                </>
            )}

            {props.customControlData.dentitie_goed_gesaneerd_value && (
                <>
                    <HorizontalFormGroupRead>
                        <FormLabel>Dentitie goed gesaneerd</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.customControlData.dentitie_goed_gesaneerd_value}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>

                    {props.customControlData.dentitie_goed_gesaneerd_remark && (
                        <HorizontalFormGroupRead>
                            <FormLabel> {} </FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.customControlData.dentitie_goed_gesaneerd_remark}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                </>
            )}

            {props.customControlData.gebitsprothese_value && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gebitsprothese</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.gebitsprothese_value}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {renderBmi(props.customControlData.gewicht, props.customControlData.lengte)}

            {props.customControlData.bmiMeasuredDate && (
                <HorizontalFormGroupRead>
                    <FormLabel>Laatste meting uitgevoerd op</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.bmiMeasuredDate}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.customControlData.bmiMeasuredBy && (
                <HorizontalFormGroupRead>
                    <FormLabel>Meting uitgevoerd door</FormLabel>
                    <InputContainer>
                        <FormParagraph>{props.customControlData.bmiMeasuredBy}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}
        </>
    );

    function showAnamnesisFields() {
        const anamnesisFields =
            props.customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter
                ? anamnesisFieldsKsyosSleep
                : anamnesisFieldsNsi;

        return anamnesisFields.map((field) => {
            const valueKey = `${field.key}_value` as keyof IOsasc1CustomControlData;
            const remarkKey = `${field.key}_remark` as keyof IOsasc1CustomControlData;
            const value = props.customControlData[valueKey];
            const remark = props.customControlData[remarkKey];

            return (
                value &&
                value === 'Nee' &&
                remark === null && (
                    <Fragment key={valueKey + remarkKey}>
                        <HorizontalFormGroupRead>
                            <FormLabel>{field.label}</FormLabel>
                            <InputContainer>
                                <FormParagraph>{value}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </Fragment>
                )
            );
        });
    }

    function showPhysicalExaminationMiniature(options: any[], selectedOption: string, alt: string) {
        const url = options.find((option) => option.value === selectedOption)?.url;

        if (url) {
            return (
                <img
                    alt={alt}
                    src={url}
                    style={{ width: '50px' }}
                />
            );
        }

        return undefined;
    }
}
